import { Modal, Form, Select, DatePicker, InputNumber, Input, Row, Col, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { usePermission } from '../../store/auth';

const MODES = ['CASH', 'CHEQUE', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'CARD', 'OTHER'];

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export interface EditablePayment {
  id: string;
  paymentNo: string;
  retailerId: string;
  paymentDate: string;
  amount: number;
  allocatedAmt: number;
  mode: string;
  referenceNo?: string | null;
  utr?: string | null;
  attachmentUrl?: string | null;
  attachmentUrls?: string[] | null;
}

/**
 * Edit a recorded payment (correct a mistake). While any of it is already applied to invoices,
 * the retailer is locked and the amount can't drop below the applied total — the backend enforces
 * the same, and adjusts the retailer ledger with compensating entries.
 */
export function EditPaymentModal({ open, onClose, payment }: { open: boolean; onClose: () => void; payment: EditablePayment | null }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const allocated = Number(payment?.allocatedAmt ?? 0);
  const isApplied = allocated > 0.009;
  const minAmount = Math.max(1, allocated);

  const { data: retailers } = useQuery({
    queryKey: ['retailers-for-payment'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: open && canViewRetailers,
  });

  // Seed via initialValues + a per-payment key (below) so the form mounts fresh each open — avoids the
  // setFieldsValue-in-effect race against the Radix-modal remount (blank fields / a prior payment's
  // retailer lingering in the shared form store → wrong PATCH target).
  const initialValues = payment
    ? {
        retailerId: String(payment.retailerId),
        paymentDate: payment.paymentDate ? dayjs(payment.paymentDate) : dayjs(),
        amount: Number(payment.amount),
        mode: payment.mode,
        referenceNo: payment.referenceNo ?? undefined,
        utr: payment.utr ?? undefined,
        attachmentUrls: (payment.attachmentUrls && payment.attachmentUrls.length)
          ? payment.attachmentUrls
          : (payment.attachmentUrl ? [payment.attachmentUrl] : []),
      }
    : undefined;

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: `/payments/${payment!.id}`,
      method: 'PATCH',
      data: {
        retailerId: vals.retailerId,
        paymentDate: vals.paymentDate ? vals.paymentDate.format('YYYY-MM-DD') : undefined,
        amount: vals.amount,
        mode: vals.mode,
        referenceNo: vals.referenceNo,
        utr: vals.utr,
        attachmentUrls: vals.attachmentUrls,
      },
    }),
    onSuccess: () => {
      message.success('Payment updated');
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['retailer-ledger'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update payment'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={payment ? `Edit payment ${payment.paymentNo}` : 'Edit payment'}
      width={680}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save changes</Button></>}
    >
      {isApplied && (
        <Alert type="info" style={{ marginBottom: 12 }}
          title={`${inr2(allocated)} of this payment is already applied to invoices.`}
          description="Retailer is locked and the amount can't go below the applied total. Reverse its allocations first to change more." />
      )}
      <Form key={payment?.id ?? 'edit'} form={form} initialValues={initialValues} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="retailerId" label="Retailer" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" disabled={isApplied}
                options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.code} · ${r.companyName}` }))} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="paymentDate" label="Date" rules={[{ required: true }]}><DatePicker className="w-full" /></Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="amount" label="Amount (₹)"
              rules={[{ required: true }, { type: 'number', min: minAmount, message: `Min ₹${minAmount.toLocaleString('en-IN')} (already applied)` }]}>
              <InputNumber style={{ width: '100%' }} min={minAmount} step={100} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="mode" label="Mode" rules={[{ required: true }]}><Select options={MODES.map((m) => ({ value: m }))} /></Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="referenceNo" label="Reference No"><Input placeholder="Cheque / UPI ref" /></Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="utr" label="UTR / Bank Ref"><Input /></Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="attachmentUrls" label="Payment proof (receipts)"
              rules={[{ required: true, type: 'array', min: 1, message: 'At least one proof is required' }]}>
              <MultiFileUploadField category="receipt" buttonLabel="Add receipts" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
